import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

class ContCardsBlock {
    constructor(root) {
        this.root = root;
        this.layout = root.querySelector('.cont-cards__layout');
        this.left = root.querySelector('.cont-cards__left');
        this.intro = root.querySelector('.cont-cards__intro');
        this.stack = root.querySelector('.cont-cards__stack');
        this.wrap = root.querySelector('.cont-cards__wrap');
        this.cards = Array.from(root.querySelectorAll('.cont-cards__card'));
        this.ready = Boolean(this.layout && this.left && this.intro && this.stack && this.wrap && this.cards.length);
        this.desktopState = {
            introTravel: 1,
            totalScroll: 1,
        };

        if (!this.ready) {
            return;
        }

        this.mm = gsap.matchMedia();
        this.init();
    }

    init() {
        this.mm.add('(min-width: 1024px)', () => this.createDesktop());
        this.mm.add('(max-width: 1023px)', () => this.createMobile());
    }

    getRotation(card, index = 0) {
        const rotation = parseFloat(card.dataset.rotation || '0');

        if (!Number.isNaN(rotation)) {
            return rotation;
        }

        return index % 2 === 0 ? 2 : -2;
    }

    getEntryRotation(rotation, index) {
        if (rotation === 0) {
            return index % 2 === 0 ? 6 : -6;
        }

        return rotation + (Math.sign(rotation) * 6);
    }

    getStickyOffset() {
        const stickyTop = parseFloat(getComputedStyle(this.root).getPropertyValue('--cont-cards-sticky-top'));

        if (!Number.isNaN(stickyTop)) {
            return stickyTop;
        }

        return 112;
    }

    getPinOffset(elementHeight) {
        const minimumOffset = this.getStickyOffset();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

        if (!viewportHeight || !elementHeight) {
            return minimumOffset;
        }

        const centeredOffset = Math.round((viewportHeight - elementHeight) * 0.5);
        const maxOffset = Math.round(viewportHeight * 0.3);

        return Math.max(minimumOffset, Math.min(centeredOffset, maxOffset));
    }

    resetLayout() {
        this.stack.style.height = '';
        this.wrap.style.height = '';

        gsap.set([this.left, this.intro, this.wrap], { clearProps: 'transform' });

        this.cards.forEach((card) => {
            card.style.position = '';
            card.style.top = '';
            card.style.left = '';
            card.style.right = '';
            card.style.zIndex = '';

            gsap.set(card, { clearProps: 'transform,transformOrigin,visibility' });
        });
    }

    formatViewportOffset(offset) {
        if (offset === 0) {
            return 'top top';
        }

        if (offset > 0) {
            return `top top+=${offset}`;
        }

        return `top top-=${Math.abs(offset)}`;
    }

    measureDesktop() {
        this.cards.forEach((card) => {
            card.style.position = '';
            card.style.top = '';
            card.style.left = '';
            card.style.right = '';
            card.style.zIndex = '';
        });

        const maxHeight = this.cards.reduce((height, card) => Math.max(height, card.offsetHeight), 0);
        const step = Math.max(Math.round(maxHeight * 1.02), 420);
        const totalScroll = step * Math.max(this.cards.length - 1, 0);
        const wrapTop = this.wrap.offsetTop;
        const stackHeight = maxHeight + wrapTop + totalScroll;

        this.wrap.style.height = `${maxHeight}px`;
        this.stack.style.height = `${stackHeight}px`;

        this.cards.forEach((card, index) => {
            card.style.position = 'absolute';
            card.style.top = '0';
            card.style.left = '0';
            card.style.right = '0';
            card.style.zIndex = String(index + 1);
        });

        const introTop = this.intro.offsetTop + this.left.offsetTop;
        const introTravel = Math.max(this.layout.offsetHeight - this.intro.offsetHeight - introTop, 0);
        const introPinTop = this.getPinOffset(this.intro.offsetHeight);
        const cardsPinTop = this.getPinOffset(maxHeight);

        this.desktopState = {
            cardsPinTop,
            introTop,
            introPinTop,
            introTravel,
            maxHeight,
            stickyTop: this.getStickyOffset(),
            totalScroll,
            wrapTop,
        };
    }

    createDesktop() {
        const refreshLayout = () => {
            this.measureDesktop();
        };

        refreshLayout();
        ScrollTrigger.addEventListener('refreshInit', refreshLayout);

        this.cards.forEach((card, index) => {
            const rotation = this.getRotation(card, index);

            gsap.set(card, {
                autoAlpha: index === 0 ? 1 : 0,
                rotation: index === 0 ? rotation : this.getEntryRotation(rotation, index),
                scale: index === 0 ? 1 : 1.03,
                y: index === 0 ? 0 : 220,
                transformOrigin: '50% 100%',
            });
        });

        const introPin = ScrollTrigger.create({
            trigger: this.layout,
            start: () => this.formatViewportOffset(this.desktopState.introPinTop),
            end: () => `+=${Math.max(this.desktopState.introTravel, 1)}`,
            pin: this.left,
            pinSpacing: false,
            invalidateOnRefresh: true,
        });

        const stagePin = ScrollTrigger.create({
            trigger: this.stack,
            start: () => this.formatViewportOffset(this.desktopState.cardsPinTop - this.desktopState.wrapTop),
            end: () => `+=${Math.max(this.desktopState.totalScroll, 1)}`,
            pin: this.wrap,
            pinSpacing: false,
            invalidateOnRefresh: true,
        });

        const timeline = gsap.timeline({
            defaults: {
                duration: 1,
                ease: 'none',
            },
            scrollTrigger: {
                trigger: this.stack,
                start: () => this.formatViewportOffset(this.desktopState.cardsPinTop - this.desktopState.wrapTop),
                end: () => `+=${Math.max(this.desktopState.totalScroll, 1)}`,
                scrub: 0.8,
                invalidateOnRefresh: true,
            },
        });

        const peekOffset = 46;
        const maxPeekOffset = 138;
        const scaleStep = 0.03;
        const opacityStep = 0.12;

        for (let i = 1; i < this.cards.length; i += 1) {
            const label = `card-${i}`;

            timeline.addLabel(label, i - 1);

            for (let j = 0; j < i; j += 1) {
                const depth = i - j;

                timeline.to(this.cards[j], {
                    y: -Math.min(depth * peekOffset, maxPeekOffset),
                    scale: Math.max(1 - (depth * scaleStep), 0.92),
                    autoAlpha: Math.max(1 - (depth * opacityStep), 0.68),
                }, label);
            }

            timeline.to(this.cards[i], {
                autoAlpha: 1,
                rotation: this.getRotation(this.cards[i], i),
                scale: 1,
                y: 0,
            }, label);
        }

        return () => {
            ScrollTrigger.removeEventListener('refreshInit', refreshLayout);

            introPin.kill();
            stagePin.kill();
            timeline.kill();
            this.resetLayout();
        };
    }

    createMobile() {
        this.resetLayout();

        const animations = this.cards.map((card, index) => {
            const rotation = this.getRotation(card, index);

            return gsap.fromTo(card, {
                autoAlpha: 0,
                rotation: rotation + (index % 2 === 0 ? 4 : -4),
                y: 56,
            }, {
                autoAlpha: 1,
                duration: 0.8,
                ease: 'power3.out',
                rotation,
                y: 0,
                scrollTrigger: {
                    trigger: card,
                    start: 'top 88%',
                    toggleActions: 'play none none reverse',
                },
            });
        });

        return () => {
            animations.forEach((animation) => {
                if (animation.scrollTrigger) {
                    animation.scrollTrigger.kill();
                }

                animation.kill();
            });

            this.resetLayout();
        };
    }
}

class ContCards {
    constructor() {
        this.blocks = Array.from(document.querySelectorAll('[data-cont-cards]'))
            .map((root) => new ContCardsBlock(root))
            .filter((block) => block.ready);

        if (!this.blocks.length) {
            return;
        }

        this.refresh = this.refresh.bind(this);
        this.scheduleRefresh = this.scheduleRefresh.bind(this);
        this.refreshTimer = null;

        if (document.readyState === 'complete') {
            this.scheduleRefresh(0);
        } else {
            window.addEventListener('load', () => this.scheduleRefresh(80), { once: true });
        }

        window.addEventListener('pageshow', () => this.scheduleRefresh(80));
    }

    scheduleRefresh(delay = 0) {
        window.clearTimeout(this.refreshTimer);
        this.refreshTimer = window.setTimeout(this.refresh, delay);
    }

    refresh() {
        const savedY = window.pageYOffset || window.scrollY || 0;

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                ScrollTrigger.sort();
                ScrollTrigger.refresh();

                const currentY = window.pageYOffset || window.scrollY || 0;

                if (Math.abs(currentY - savedY) > 1) {
                    window.scrollTo(0, savedY);
                    ScrollTrigger.refresh();
                }
            });
        });
    }
}

export default ContCards;
